<template>
  <div>
    <v-card style="padding-bottom: 20px" class="pr-5" tile>
      <v-card-actions class="pl-5 py-3 d-flex justify-space-between">
        <div class="d-flex align-center">
          <span class="pr-2">
            <v-btn style="text-decoration:none !important;" :disabled="selected.length <= 0" @click="showSelected=true"
                small
                outlined 
                color="primary">View Selected</v-btn>
          </span>
          <span>
            <cluster-select @clearSelections="selected = [], selectAll=false, selectedEntities = []" :disabled="notSelected" :entityType="'supplier'" :entityIDType="'supplier_id'" :entities="selected" :entity-labels="clusterEntityLabels" />
          </span>
        </div>
        <div class="d-flex align-center pr-2 pb-5 pt-4">
          <span class="mr-4"> 
          <v-btn-toggle v-model="viewMode" mandatory class="view-toggle" @change="saveViewMode" color="primary">
            <v-btn small value="list">
              LIST VIEW
            </v-btn>
            <v-btn small value="card">
              CARD VIEW
            </v-btn>
          </v-btn-toggle>
          </span>
        </div>
      </v-card-actions>
      <!-- Card View -->
      <div v-if="viewMode === 'card'" class="card-view-container">
        <v-skeleton-loader
          v-if="isLoading"
          type="card"
          class="mb-4"
        ></v-skeleton-loader>
        <v-row v-else>
          <v-col
            v-for="item in entities"
            :key="item.id"
            cols="12"
            md="4"
            sm="6"
          >
            <v-card class="supplier-card" elevation="2" @click="entity_selected(item.id)" style="cursor: pointer; height: 100%;">
              <v-card-text class="pb-2">
                <div class="d-flex justify-space-between mb-2 card-header-row">
                  <div class="d-flex align-center" style="flex: 1; min-width: 0;">
                    <CompanyLogo
                      v-if="item.website"
                      :domain="item.website"
                      :company-name="item.name"
                      size="small"
                      class="mr-3 flex-shrink-0"
                    />
                    <div class="flex-grow-1" style="min-width: 0;">
                      <div class="d-flex align-center">
                        <h3 class="subtitle-1 font-weight-bold mb-0" style="line-height: 1.2;">
                          <a 
                            :href="$router.resolve({ name: 'supplier', params: { id: item.id } }).href"
                            @click.stop
                            target="_blank"
                            class="text-decoration-none"
                          >
                            {{ item.name }}
                          </a>
                        </h3>
                      </div>
                      <!-- <div v-if="item.metadata && item.metadata.industry && item.metadata.industry.length > 0" class="caption grey--text mb-2">
                        {{ item.metadata.industry[0] }}
                      </div>
 -->
                      <div v-if="item.metadata && item.metadata.industry" class="mt-1">
                        <span v-for="industry in item.metadata.industry" :key="industry">
                          <router-link target="_blank" :to="{ name: 'industry', query: { supplier_industry: industry } }">
                            <v-chip
                              class="my-1 ml-0 mr-2"
                              color="secondary"
                              label
                              text-color="secondary"
                              small
                              outlined
                            >
                              <v-icon small left>mdi-label</v-icon>
                              {{ industry }}
                            </v-chip>
                          </router-link>
                        </span>
                      </div>
                    </div>
                  </div>
                  <div class="d-flex align-center flex-shrink-0 ml-2 card-actions-row">
                    <v-tooltip top dark color="#000000" style="opacity: 0.7;">
                      <template v-slot:activator="{ on }">
                        <v-btn
                          v-on="on"
                          icon
                          medium
                          color="#4EB888"
                          light
                          :href="item.website"
                          target="_blank"
                          @click.stop
                          style="text-decoration: none !important;"
                          :disabled="disableIcon(item.website)"
                          class="mr-1 mt-1"
                        >
                          <v-icon medium>mdi-web</v-icon>
                        </v-btn>
                      </template>
                      <span>Go to Website</span>
                    </v-tooltip>
                    <v-checkbox
                      @click.stop
                      v-model="selected"
                      :value="item.id"
                      hide-details
                      class="mt-0"
                    ></v-checkbox>
                  </div>
                </div>

                <!-- Description -->
                <div v-if="item.metadata && item.metadata.description" class="body-2 mb-3 text--secondary" style="line-height: 1.5;">
                  {{ truncateDescription(item.metadata.description, 150) }}
                </div>

                <!-- Financial Data -->
                <div class="financial-data mt-3 mb-2">
                  <div class="d-flex justify-space-between align-center mb-1">
                    <span class="caption grey--text">Awarded (All Time)</span>
                    <span class="body-2 font-weight-medium">
                      <router-link 
                        :to="{ name: 'search', query: { supplier_id: item.id, publisher_id: publisher_id } }"
                        @click.stop
                        class="text-decoration-none"
                      >
                        {{ formattedCurrency(item.award_total) }}
                      </router-link>
                    </span>
                  </div>
                  <div class="d-flex justify-space-between align-center mb-1">
                    <span class="caption grey--text">Contracts</span>
                    <span class="body-2 font-weight-medium">
                      <router-link 
                        :to="{ name: 'search', query: { supplier_id: item.id, publisher_id: publisher_id } }"
                        @click.stop
                        class="text-decoration-none"
                      >
                        {{ numFormatter(item.num_notices) }}
                      </router-link>
                    </span>
                  </div>
                  <div v-if="item.growth && item.growth.current_year_total" class="d-flex justify-space-between align-center">
                    <span class="caption grey--text">Awarded (Last 12M)</span>
                    <span class="body-2 font-weight-medium">{{ formattedCurrency(item.growth.current_year_total) }}</span>
                  </div>
                </div>

                <!-- Tags -->
                <div v-if="item.metadata && item.metadata.smartTags && item.metadata.smartTags.length > 0" class="tags-section mt-3">
                  <v-chip
                    v-for="tag in item.metadata.smartTags.slice(0, 3)"
                    :key="tag"
                    class="ma-1"
                    color="primary"
                    label
                    small
                    outlined
                  >
                    {{ tag }}
                  </v-chip>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
        <div class="text-center mt-4">
          <v-pagination
            v-if="entities.length > 1"
            v-model="page_num"
            color="#4EB888"
            :length="num_pages"
            total-visible="0"
            prev-icon="mdi-menu-left"
            next-icon="mdi-menu-right"
            @input="
              $router.replace({ name: $route.name, query: {...$route.query, page: page_num } })"
          ></v-pagination>
        </div>
      </div>

      <!-- List View -->
      <div v-else>
      <!-- <v-skeleton-loader
        v-if="isLoading"
        type="table-tbody"
      ></v-skeleton-loader> -->
    
      <v-data-table
        :headers="headers"
        :items="entities"
        :loading="isLoading"
        :items-per-page="entities.length"
        hide-default-footer
        class="elevation-0"
      >
        <!-- Checkbox column -->
        <template slot="item.checkbox" slot-scope="{ item }">
          <v-checkbox
            @click="select(), updateSelectedEntities()"
            v-model="selected"
            :value="item.id"
          ></v-checkbox>
        </template>

        <!-- Header template for HTML rendering -->
        <template slot="header.award_total">
          <div class="text-center">
            <div>Awarded</div>
            <div class="caption" style="font-weight:normal !important;">(All Time)</div>
          </div>
        </template>

        <template slot="header.num_notices">
          <div class="text-center">
            <div>Contracts</div>
            <div class="caption" style="font-weight:normal !important;">(All Time)</div>
          </div>
        </template>

        <template slot="header.current_year_total">
          <div class="text-center">
            <div>Awarded</div>
            <div class="caption" style="font-weight:normal !important;">(Last 12M)</div>
          </div>
        </template>

        <template slot="header.curr_yr_avg_contract">
          <div class="text-center">
            <div>Avg Contract</div>
            <div class="caption" style="font-weight:normal !important;">(12M)</div>
          </div>
        </template>

        <template slot="header.current_year_vol">
          <div class="text-center">
            <div>New Contracts</div>
            <div class="caption" style="font-weight:normal !important;">(12M)</div>
          </div>
        </template>

        <template slot="header.next_year_total">
          <div class="text-center">
            <div>Expiring Contracts</div>
            <div class="caption" style="font-weight:normal !important;">(Next 12M)</div>
          </div>
        </template>

        <!-- Name column with metadata -->
        <template slot="item.name" slot-scope="{ item }">
          <div class="py-2 d-flex align-top">
            <!-- Company Logo -->
            <CompanyLogo
              v-if="item.website"
              :domain="item.website"
              :company-name="item.name"
              size="small"
              class="mr-3"
            />
            
            <div class="flex-grow-1">
              <div class="subtitle-2 mb-2">
                <a 
                  :href="$router.resolve({ name: 'supplier', params: { id: item.id } }).href"
                  target="_blank"
                  class="text-decoration-none"
                >
                  {{ item.name }}
                </a>
              </div>
            
              <!-- Alternative name -->
              <!-- <div v-if="item.published_name && item.published_name != item.name" class="caption text-truncate mb-2">
                Alt. {{ item.published_name }}
                </div> -->

              <!-- Metadata display -->
              <div v-if="hasMetadata(item)" class="metadata-section">
                <!-- Industry -->
                <span v-if="item.metadata && item.metadata.industry">
                  <span v-for="industry in item.metadata.industry" :key="industry">
                    <router-link target="_blank" :to="{ name: 'industry', query: { supplier_industry: industry } }">
                      <v-chip
                        class="my-1 ml-0 mr-2"
                        color="secondary"
                        label
                        text-color="secondary"
                        small
                        outlined
                      >
                        <v-icon small left>mdi-label</v-icon>
                        {{ industry }}
                      </v-chip>
                    </router-link>
                  </span>
                </span>

               <!--  Smart Tags -->
                <!-- <span v-if="item.metadata && item.metadata.smartTags">
                  <span v-for="tag in item.metadata.smartTags" :key="tag">
                    <v-chip
                      class="my-1 ml-0 mr-2"
                      color="primary"
                      label
                      text-color="primary"
                      small
                      outlined
                    >
                      <v-icon small left>mdi-tag</v-icon>
                      {{ tag }}
                    </v-chip>
                  </span>
                </span> -->

                <!-- Clusters -->
                <span v-for="clstr in item.clusters" :key="clstr.id">
                  <router-link target="_blank" :to="{ name: 'cluster', params: { clusterId: clstr.id } }">
                    <v-chip
                      v-if="clstr"
                      class="my-1 ml-0 mr-2"
                      color="primary"
                      label
                      text-color="primary"
                      small
                      outlined
                    >
                      <v-icon small left>mdi-hexagon-multiple</v-icon>
                      {{ clstr.name }}
                    </v-chip>
                  </router-link>
                </span>

                <!-- Description preview -->
                <!-- <div v-if="item.metadata && item.metadata.description" class="caption">
                  {{ item.metadata.description.substring(0, 1000) }}{{ item.metadata.description.length > 1000 ? '...' : '' }}
                </div> -->
              </div>
            </div>
          </div>
        </template>

        <!-- State column -->
        <!-- <template slot="item.state" slot-scope="{ item }">
          <span>{{ item.state }}</span>
        </template> -->

        <!-- Award Total column -->
        <template slot="item.award_total" slot-scope="{ item }">
          <div class="text-center">
            <router-link
              :to="{ name: 'search', query: { supplier_id: item.id, publisher_id: publisher_id } }"
            >
              {{ formattedCurrency(item.award_total) }}
            </router-link>
          </div>
        </template>

        <!-- Number of Notices column -->
        <template slot="item.num_notices" slot-scope="{ item }">
          <div class="text-center">
            <router-link
              :to="{ name: 'search', query: { supplier_id: item.id, publisher_id: publisher_id } }"
            >
              {{ numFormatter(item.num_notices) }}
            </router-link>
          </div>
        </template>

        <!-- Current Year Total column -->
        <template slot="item.current_year_total" slot-scope="{ item }">
          <div class="text-center">
            <div>{{ formattedCurrency(item.growth.current_year_total) }}</div>
            <div>
              <v-icon class="py-0 pr-0 mr-0" :color="growth_format(item.growth.spend_change_percent).color">
                {{ growth_format(item.growth.spend_change_percent).icontype }}
              </v-icon>
              {{ growth_format(item.growth.spend_change_percent).change }}%
            </div>
          </div>
        </template>

        <!-- Current Year Avg Contract column -->
        <template slot="item.curr_yr_avg_contract" slot-scope="{ item }">
          <div class="text-center">
            <div>{{ formattedCurrency(item.growth.curr_yr_avg_contract) }}</div>
            <div>
              <v-icon class="py-0 pr-0 mr-0" :color="growth_format(item.growth.avg_change_percent).color">
                {{ growth_format(item.growth.avg_change_percent).icontype }}
              </v-icon>
              {{ growth_format(item.growth.avg_change_percent).change }}%
            </div>
          </div>
        </template>

        <!-- Current Year Volume column -->
        <template slot="item.current_year_vol" slot-scope="{ item }">
          <div class="text-center">
            <div>{{ numFormatter(item.growth.current_year_vol) }}</div>
            <div>
              <v-icon class="py-0 mr-0 pr-0" :color="growth_format(item.growth.vol_change_percent).color">
                {{ growth_format(item.growth.vol_change_percent).icontype }}
              </v-icon>
              {{ growth_format(item.growth.vol_change_percent).change }}%
            </div>
          </div>
        </template>

        <!-- Next Year Total column -->
        <template slot="item.next_year_total" slot-scope="{ item }">
          <div class="text-center">
            <div>{{ formattedCurrency(item.expiring_soon.next_year_total) }}</div>
            <div>
              <v-icon class="py-0 mr-0 pr-0" :color="growth_format(item.expiring_soon.spend_change_percent).color">
                {{ growth_format(item.expiring_soon.spend_change_percent).icontype }}
              </v-icon>
              {{ growth_format(item.expiring_soon.spend_change_percent).change }}%
            </div>
          </div>
        </template>
      </v-data-table>

      <div class="text-center">
        <v-pagination
          v-if="entities.length > 1"
          v-model="page_num"
          color="#4EB888"
          :length="num_pages"
          total-visible="0"
          prev-icon="mdi-menu-left"
          next-icon="mdi-menu-right"
          @input="
            $router.replace({ name: $route.name, query: {...$route.query, page: page_num } })"
        ></v-pagination>
        
      </div>
      </div>
    </v-card>

    <v-dialog v-model="showSelected" width="50%">
      <v-card>
        <v-list
        
        flat
      >
      <span><v-subheader>Currently selected entities</v-subheader></span><span> 
            <v-btn class="caption ml-2" x-small text color="primary" 
                  :disabled="selected.length <= 0" 
                  @click="(selectAll = false), user_clicked_select_all(), showSelected=false">
              Clear All
            </v-btn></span>
      <v-btn 
        absolute
        top
        right
        icon
        @click="showSelected = false"
        medium
      >
        <v-icon medium>
          mdi-close
        </v-icon>
      </v-btn>

      <v-card flat class="px-2 py-2 mx-2 my-2" min-height="300" max-height="420" style="flex-grow: 1 !important; overflow:auto !important;">

          <v-row
            align="center"
            justify="start"
          >
            <v-col
              v-for="entity in selectedEntities"
              :key="entity.id"
              class="py-1"
            >
              <v-chip
                color="primary"
                small
                label
                clearable
                dense
                close
                dark
                @click:close="deselectEntity(entity.id)"
              >
                {{ entity.name }}
              </v-chip>
            </v-col>
          </v-row>
      </v-card>
      
    </v-list>
    </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { bus } from "@/main.js";
import { fetchSupplierDirectory } from "../APIManager";
import { growth_format, formattedCurrency, numFormatter } from "../Utils.js";
import ClusterSelect from "../ClusterSelect.vue";
import CompanyLogo from "../common/CompanyLogo.vue";

export default {
  components: {
    ClusterSelect,
    CompanyLogo
  },

  props: {
    publisher_id: Number,
    supplier_id: Number,
    filters: {}
  },
  data() {
    return {
      showSelected: false,
      saveButtonActive: false,
      clusterbox: false,
      selectAll: false,
      selected: [],
      selectedCount: 0,
      item: 0,
      entities: [],
      entities_temp: [],
      page_num: 1,
      num_pages: 5,
      isLoading: true,
      entitiesViewedCount: 0,
      selectedEntities: [],
      viewMode: 'list' // 'list' or 'card'
      //current_selection:
    };
  },

  mounted() {
    // Load view mode from localStorage if available, otherwise default to 'list'
    const savedViewMode = localStorage.getItem('supplierViewMode');
    if (savedViewMode === 'list' || savedViewMode === 'card') {
      this.viewMode = savedViewMode;
    }
    //this.page_num = this.$route.query.page;
    this.buildQuery();
  },

  methods: {

    growth_format,
    numFormatter,
    formattedCurrency,

    buildQuery(){
      // Use fetchSupplierDirectory for all queries
      if (this.$route.query["page"]){
        this.page_num = this.$route.query["page"];
        this.loadSupplierDirectory(this.page_num, this.$route.query);
      }
      else{
        this.page_num = 1;
        this.loadSupplierDirectory(this.page_num, this.$route.query);
      }
    },

    entity_selected(entity_id) {
      //var ent = JSON.stringify(entity);
      const route = this.$router.resolve({ name: "supplier", params: { id: entity_id } });
      window.open(route.href, '_blank');
      //bus.$emit("entity-selected", entity);
    },

    loadSupplierDirectory(page_num, query) {
      //////////////////console.log("loading suppliers: ");
      this.isLoading = true;

      fetchSupplierDirectory(page_num, query).then(results => {
        this.entities = results.data;
        this.entitiesViewedCount += this.entities.length; // num entities across directory pages
        this.num_pages = results.stats.total_pages;
        this.page_num = results.stats.page_no;
        this.select_all();
        this.isLoading = false;

        //////////////////console.log("fetchSupplierDirectory: ", results);
      });
    },

    disableIcon(url) {
      if (!url) {
        return true;
      } else {
        return false;
      }
    },

    select_all(){
      if (this.selectAll) {
          for (let entity in this.entities) {
            
            //check the entity id is not already in the selected entities
            let index = this.selected.findIndex(x => x === this.entities[entity].id);
            ////////////////console.log (index,  this.entities[entity].id);
            if (index < 0){
              this.selected.push(this.entities[entity].id);
              this.updateSelectedEntities();
            }
            
          }
      }
    },

    user_clicked_select_all(){
      this.userClickedSelectAll = this.selectAll;
      if (!this.selectAll){ //if the user has physically unclicked selectall
        this.selected = [];
        this.selectedEntities = [];
      }
    },
    
    select() {
        this.selectAll = false;
    },

    updateSelectedEntities(){
      ////////////console.log ("selected :", this.selected);
      for (let s in this.selected) { 
        //check the entity id is not already in the selected entities
        let entity = this.entities.find(x => x.id === this.selected[s]);
        ////////////////console.log (index,  this.entities[entity].id);

        if (entity){
          let index = this.selectedEntities.findIndex(x => x.id === entity.id);
          if (index < 0) {
            this.selectedEntities.push(entity);
          }

        }
      }
    },

    deselectEntity(id){

      let index = this.selected.findIndex(x => x === id);
      ////////////////console.log (index,  this.entities[entity].id);
      if (index >= 0) { // only splice array when item is found
        this.selected.splice(index, 1); // 2nd parameter means remove one item only
      }

      let entityIndex = this.selectedEntities.findIndex(x => x.id === id);

      if (entityIndex >= 0) { // only splice array when item is found
        this.selectedEntities.splice(entityIndex, 1); // 2nd parameter means remove one item only
      }
      this.selectAll = false;
    },

    hasMetadata(item) {
      return item && item.metadata && (
        (item.metadata.industry && item.metadata.industry.length > 0) ||
        (item.metadata.smartTags && item.metadata.smartTags.length > 0) ||
        (item.metadata.description) ||
        (item.clusters && item.clusters.length > 0)
      );
    },

    truncateDescription(text, maxLength) {
      if (!text) return '';
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength).trim() + '...';
    },

    saveViewMode() {
      // Persist view mode to localStorage
      localStorage.setItem('supplierViewMode', this.viewMode);
    },

  },
  created() {
    //this.loadPublishers(this.page_num);
  },

  computed: {
    notSelected(){
      if (this.selected.length > 0) return false;
      else return true;
    },
    clusterEntityLabels() {
      const labels = {};
      for (const entity of this.selectedEntities) {
        if (entity?.id != null && entity?.name) {
          labels[entity.id] = entity.name;
        }
      }
      return labels;
    },
    
    headers() {
      return [
        { text: '', value: 'checkbox', sortable: false, width: '50px' },
        { text: 'Supplier', value: 'name', sortable: true },
        //{ text: 'State', value: 'state', sortable: true },
        { text: 'Awarded', value: 'award_total', sortable: true },
        { text: 'Contracts', value: 'num_notices', sortable: true },
        { text: 'Awarded', value: 'current_year_total', sortable: true },
        { text: 'Avg Contract', value: 'curr_yr_avg_contract', sortable: true },
        { text: 'New Contracts', value: 'current_year_vol', sortable: true },
        { text: 'Expiring Contracts', value: 'next_year_total', sortable: true }
      ];
    }
  },
  watch: {

    $route(to, from) {
      //////////////////console.log(to);
      this.buildQuery();
    }
  }
};
</script>
<style>
.wrap-text {
  word-wrap: normal !important;
}

.chip-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;
}
.nav-list {
  background-color: "#303030";
  border-bottom: 2pt white;
}
.list-item {
  text-decoration: none !important;
  color: "#FFFFFF" !important;
}

.bottom-border {
  border-bottom: 2pt white;
}

/* Card View Styles */
.card-view-container {
  padding: 16px;
}

.supplier-card {
  transition: transform 0.2s, box-shadow 0.2s;
}

.supplier-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.financial-data {
  border-top: 1px solid #e0e0e0;
  border-bottom: 1px solid #e0e0e0;
  padding: 12px 0;
}

.view-toggle .v-btn {
  text-transform: uppercase;
  font-weight: 500;
  letter-spacing: 0.5px;
}

.view-toggle .v-btn--active {
  background-color: var(--v-primary-base, #1976d2) !important;
  color: white !important;
}

.card-header-row {
  align-items: flex-start;
}

.card-header-row .card-actions-row {
  align-items: center;
  align-self: flex-start;
  margin-top: 0;
}

/* Ensure company name and actions align on the same baseline */
.card-header-row h3 {
  display: inline-block;
  vertical-align: middle;
}

.card-actions-row {
  align-items: center;
}

/* On smaller screens, maintain alignment */
@media (max-width: 960px) {
  .card-header-row {
    align-items: flex-start;
  }
  
  .card-header-row .card-actions-row {
    align-items: center;
    margin-top: 0;
    padding-top: 0;
  }
}
</style>
